import api from "../services/api.js";
import Vue from "vue";
import { provinceSettings } from "../services/preferences";

export const state = () => ({
  provincialSettings: provinceSettings,
  provincialData: {},
  communities: null,
  generalData: null,
  selectedDistrict: {},
  lastUpdated: {}
});

export const actions = {
  async getNewRssFeed({ commit }) {
    await commit("LOAD_DATA", "polling");
    const resp = await api.get("index.rss");
    const items = resp.rss.channel.item;
    await commit("SET_LAST_UPDATED");
    const sortedPerProvince = {};
    for (const community of items.filter(
      item => item["media:provincie"]._text
    )) {
      const pKey = community["media:provincie"]._text;
      if (community.title._attributes && community.title._attributes.cbs) {
        const cKey = community.title._attributes.cbs; // get guid
        if (!(pKey in sortedPerProvince)) {
          sortedPerProvince[pKey] = { [cKey]: community };
        } else {
          sortedPerProvince[pKey][cKey] = community;
        }
      }
    }
    await commit("SET_COMMUNITIES", sortedPerProvince);
    const generalData = {};
    for (const data of items.filter(item => !item["media:provincie"]._text)) {
      generalData[data.title._text.replace(/-|\s/g, "_").toLowerCase()] = data;
    }
    await commit("SET_GENERAL_DATA", generalData);
    await commit("LOAD_DATA_SUCCESS", "polling");

    return Promise.resolve();
  },
  async fetchProvincialData({ commit }, regionCode) {
    const data = { demographic: {}, seats: {} };
    await api.get(regionCode + ".xml").then(resp => {
      data.demographic = resp.Uitslag;
    });
    await api.get(Number(regionCode) + 100 + ".xml").then(resp => {
      data.seats = resp;
    });
    commit("SET_PROVINCIAL_DATA", data);
    return data;
  },
  async getDistrictStats({ commit, state }, district) {
    if (state.loading.district) {
      console.warn("a district is already being requested");
      return;
    }
    commit("LOAD_DATA", "district");
    try {
      const resp = await api.get(district);
      resp.Uitslag.guid = district;
      await commit("SET_ACTIVE_COMMUNITY", resp.Uitslag);
      commit("LOAD_DATA_SUCCESS", "district");
      return Promise.resolve(resp);
    } catch (err) {
      commit("LOAD_DATA_FAILED", "district");
      return Promise.reject(err);
    }
  },
  async resetDistrictStats({ commit }) {
    commit("SET_ACTIVE_COMMUNITY", {});
  }
};

export const mutations = {
  LOAD_DATA(state, key) {
    Vue.set(state, "loading", { ...state.loading, [key]: true });
  },
  LOAD_DATA_SUCCESS(state, key) {
    Vue.delete(state.loading, key);
  },
  LOAD_DATA_FAILED(state, key) {
    Vue.delete(state.loading, key);
  },
  SET_ACTIVE_COMMUNITY(state, data) {
    Vue.set(state, "selectedDistrict", data);
  },
  SET_LAST_UPDATED(state) {
    state.lastUpdated.rss = new Date();
  },
  SET_COMMUNITIES(state, c) {
    // parse without getters to stress memmory less
    Vue.set(state, "communities", JSON.parse(JSON.stringify(c)));
  },
  SET_PROVINCIAL_DATA(state, data) {
    Vue.set(state, "provincialData", data);
  },
  SET_GENERAL_DATA(state, data) {
    Vue.set(state, "generalData", data);
  }
};

export const getters = {
  provincialPartySeats(state, getters) {
    if (state.provincialData.seats) {
      const seats = state.provincialData.seats.Uitslag;
      if (!Array.isArray(seats.Groep)) {
        // seats.Group must be an array
        return null;
      }
      const d = seats.Groep.filter(g => g._attributes.type === "Partij");
      const formatResults = type => {
        const data = d.map(p =>
          Number(p.Verkiezing[type === "new" ? 0 : 1].Zetels._text)
        );
        return data;
      };
      const stats = {
        labels: d.map(p => p.KorteNaam._text),
        meta: {
          fullNames: d.map(p => ({ [p.KorteNaam._text]: p.Naam._text }))
        },
        labelPrefix: "Zetels",
        scale: 15,
        unit: " zetels",
        datasets: [
          {
            values: formatResults("old"),
            label: getters.getElectionYears[1],
            color: "grey"
          }, // results new
          {
            values: formatResults("new"),
            label: getters.getElectionYears[0],
            color: getters.getProvinceSetting.color
          } // results old
        ]
      };
      return stats;
    }
    return null;
  },
  votingBehavior: (state, getters) => (district = false) => {
    let resp = district
      ? state.selectedDistrict
      : state.provincialData.demographic;
    if (!resp.Groep) {
      // fallback so null will never be returned (this hides the data suddenly)
      resp = district
        ? state.provincialData.demographic
        : state.selectedDistrict;
      if (!resp.Groep) {
        return;
      }
    }
    if (resp.Groep) {
      const turnout = type =>
        resp.Groep.find(g => g.KorteNaam._text === "Opkomst").Verkiezing[
          type === "new" ? 0 : 1
        ].Perc._text.replace(",", ".");

      const chosersData = {
        labels: getters.getElectionYears,
        labelPrefix: "Opkomst",
        unit: "%",
        legend: false,
        scale: 100,
        datasets: [
          {
            values: [Number(turnout("new")), Number(turnout("old"))],
            color: [getters.getProvinceSetting.color, "grey"]
          }
        ]
      };
      return chosersData;
    }
  },
  getPartyResults: (state, getters) => data => {
    if (data && Object.keys(data).length) {
      const d = data.Groep.filter(
        g =>
          g._attributes.type === "Partij" &&
          // remove everything with a percentage of 1.5 or lower
          (Number(g.Verkiezing[0].Perc._text.replace(",", ".")) > 1.5 ||
            Number(g.Verkiezing[1].Perc._text.replace(",", ".")) > 1.5)
      );

      const formatResults = type => {
        const data = d.map(p =>
          Number(
            p.Verkiezing[type === "new" ? 0 : 1].Perc._text.replace(",", ".")
          )
        );
        return data;
      };
      const stats = {
        labels: d.map(p => p.KorteNaam._text),
        meta: {
          fullNames: d.map(p => ({ [p.KorteNaam._text]: p.Naam._text }))
        },
        unit: "%",
        datasets: [
          {
            values: formatResults("old"),
            label: getters.getElectionYears[1],
            color: "grey"
          }, // results new
          {
            values: formatResults("new"),
            label: getters.getElectionYears[0],
            color: getters.getProvinceSetting.color
          } // results old
        ]
      };
      return stats;
    }
  },
  districtPartyResults(state, getters) {
    return getters.getPartyResults(state.selectedDistrict);
  },
  provincialPartyVotes(state, getters) {
    return getters.getPartyResults(state.provincialData.demographic);
  },
  getCompletedDistricts: state => district => ({
    completed: Object.values(state.communities[district]).filter(
      c => c["media:uitslagstatus"]._text === "eindstand"
    ).length,
    total: Object.keys(state.communities[district]).length
  }),
  getProvinceSetting(state) {
    if (state.provincialData.demographic) {
      return state.provincialSettings[
        state.provincialData.demographic._attributes.anpNr
      ];
    }
    return null;
  },
  getProvincialTotals: state => (district = false) => {
    let resp = district
      ? state.selectedDistrict
      : state.provincialData.demographic;
    if (!resp.Groep) {
      // fallback so null will never be returned (this hides the data suddenly)
      resp = district
        ? state.provincialData.demographic
        : state.selectedDistrict;
      if (!resp.Groep) {
        return;
      }
    }
    const totals = resp.Groep.filter(g => g._attributes.type === "Totaal");
    const totalObject = {};
    //
    for (const total of totals) {
      total.Verkiezing.forEach(v => {
        if (!totalObject[v._attributes.code]) {
          totalObject[v._attributes.code] = {};
        }
        totalObject[v._attributes.code][
          total.Naam._text.replace(/\s/, "_").toLowerCase()
        ] = v;
      });
    }
    return totalObject;
  },
  getElectionYears(state) {
    // get the two election years based on the seats data
    if (state.provincialData.seats) {
      return state.provincialData.seats.Uitslag.Groep[0].Verkiezing.map(v =>
        v._attributes.code.replace("PS", "20")
      );
    }
    return ["2019", "2015"];
  }
};
